import {
	Box,
	Button,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	Typography,
} from '@mui/material';
import { closeSnackbar, useSnackbar } from 'notistack';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import { GridColDef } from '@mui/x-data-grid';
import { showLabel } from '@/utils/helper';
import { ExerciseType, OnoffType, QualityType } from '@/api/enum/common.enum';
import { DefaultValues, FormProvider, useForm } from 'react-hook-form';
import AppDataGrid, {
	initialPagination,
} from '@/common/components/dataGrid/AppDataGrid';
import AppSummarySearchBar from '@/common/components/form-inputs/AppSummarySearchBar';
import { useSetDingMutation } from '@/api/exercise/dingDay21ApiSlice';
import dayjs from 'dayjs';
import { DISPLAY_DATETIME_12H_FORMAT } from '@/utils/dateHelper';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import { useGetRegularSeriesListQuery } from '@/api/task/regularApiSlice';
import {
	RegularFilter,
	TaskExerciseSeriesDetailDTO,
} from '@/api/DTO/task/regular.interface';
import PublishedChip from '@/common/components/PublishedChip';
import { PUBLISHED_LISTA, PUBLISHED_LISTB } from '@/common/constants/common';

interface Props {
	handleGetSeries: (obj: any) => void;
}

export default function AddSeriesDialog(props: Props) {
	const { handleGetSeries } = props;
	const { t } = useTranslation();
	const { enqueueSnackbar } = useSnackbar();
	const [open, setOpen] = useState(false);
	const [selectedData, setSelectedData] = useState<any[]>([]);

	useEffect(() => {
		setSelectedData([]);
	}, [open]);

	const exerciseTypeList = [
		{ id: ExerciseType.Day21, name: t('drawerListItems.day21') },
		{ id: ExerciseType.Meditation, name: t('drawerListItems.meditation') },
		{ id: ExerciseType.Body, name: t('drawerListItems.body') },
		{ id: ExerciseType.Crossover, name: t('drawerListItems.crossover') },
		{ id: ExerciseType.Major, name: t('drawerListItems.marjorExercise') },
		{ id: ExerciseType.Addition, name: t('day21.additional') },
	];
	const defaultPropertyFilter: RegularFilter = {
		pagination: initialPagination,
		searchString: '',
		sortings: [],
	};
	const defaultValues: DefaultValues<RegularFilter> = defaultPropertyFilter;
	const methods = useForm<RegularFilter>({
		defaultValues,
	});

	const { data: propertySummary, isSuccess: isGetPropertySummarySuccess } =
		useGetRegularSeriesListQuery(methods.getValues(), {
			refetchOnMountOrArgChange: true,
			skip: !open,
		});

	useEffect(() => {}, [methods.watch()]);
	const onSubmit = async () => {
		handleGetSeries(selectedData);
		handleClose();
	};

	const handleClose = () => {
		setOpen(false);
	};

	const columns: GridColDef[] = [
		{
			field: 'seriesTitleZh',
			headerName: t('meditation.title'),
			flex: 21,
		},
		{
			field: 'exerciseType',
			headerName: t('moment.exerciseType'),
			flex: 21,
			renderCell: (params: any) =>
				showLabel(params.row.exerciseType, exerciseTypeList),
		},
		{
			field: 'exerciseCount',
			headerName: t('meditation.exerciseCount'),
			flex: 21,
		},
		{
			field: 'published',
			headerName: t('meditation.status'),
			flex: 21,
			renderCell: (params: any) => (
				<PublishedChip
					published={params.row.published}
					publishList={PUBLISHED_LISTA}
				/>
			),
		},
		{
			field: 'updatedAt',
			headerName: t('taskManage.updateTime'),
			flex: 21,
		},
	];

	const handlechange = (text: string) => {
		methods.reset({ ...defaultPropertyFilter, searchString: text });
	};

	const handleSelect = (rows: string[]) => {
		if (rows.length > 1) {
			return enqueueSnackbar(t('exerciseSetting.max1'), {
				variant: 'warning',
				anchorOrigin: { horizontal: 'center', vertical: 'top' },
				autoHideDuration: 3000,
				action: (key) => {
					return <CloseIcon onClick={() => closeSnackbar(key)} />;
				},
			});
		}
		const arr = [
			{
				seriesId: rows[0],
				seriesTitleZh: propertySummary?.data?.items.find(
					(item) => item.seriesId === rows[0]
				)?.seriesTitleZh,
			},
		];

		setSelectedData(rows.length > 0 ? arr : []);
	};

	const transformResponse = useCallback(
		(response?: TaskExerciseSeriesDetailDTO[]) => {
			if (!response) return;
			const newResponse: any[] = [];
			response.forEach((item) => {
				newResponse.push({
					...item,
					updatedAt: !item.updatedAt
						? '-'
						: dayjs(item.updatedAt)
								.locale('en')
								.format(DISPLAY_DATETIME_12H_FORMAT),
				});
			});

			return newResponse;
		},
		[]
	);

	const formContent = (
		<>
			<Button
				onClick={() => setOpen(true)}
				endIcon={<AddCircleOutlineOutlinedIcon />}
			>
				{t('taskManage.addSeries')}
			</Button>
			<Dialog
				open={open}
				onClose={() => handleClose()}
				aria-labelledby='alert-dialog-title'
				aria-describedby='alert-dialog-description'
				maxWidth='lg'
				fullWidth
			>
				<DialogTitle variant='h5'>{t('taskManage.addSeries')}</DialogTitle>
				<IconButton
					aria-label='close'
					onClick={() => handleClose()}
					sx={{
						position: 'absolute',
						right: 8,
						top: 8,
						color: (theme) => theme.palette.grey[500],
					}}
				>
					<CloseIcon />
				</IconButton>
				<DialogContent>
					<Box
						display='flex'
						justifyContent='space-between'
						alignItems='center'
						marginBottom={'10px'}
					>
						<Typography
							variant='body2'
							sx={{
								display: 'flex',
							}}
						>
							<ErrorOutlineIcon sx={{ fontSize: '20px' }} />
							&nbsp;
							{t('taskManage.onlyOne')}
						</Typography>
					</Box>
					<FormProvider {...methods}>
						<AppSummarySearchBar
							name='searchString'
							control={methods.control}
							placeholder={t('common.search')}
							onchange={(text) => {
								handlechange(text);
							}}
						/>
						<div style={{ height: '20px' }}></div>
						<AppDataGrid
							checkboxSelection
							loading={!isGetPropertySummarySuccess}
							rows={transformResponse(propertySummary?.data?.items) ?? []}
							rowCount={propertySummary?.data?.pagination.totalItems ?? 0}
							columns={columns}
							getRowId={(row) => row.seriesId}
							onSelectionModelChange={(selectionModel) => {
								handleSelect(selectionModel as string[]);
							}}
							selectionModel={selectedData.map(
								(item: { seriesId: any }) => item.seriesId
							)}
						/>
					</FormProvider>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => handleClose()}>{t('button.cancel')}</Button>

					<Button type='submit' variant='contained' onClick={() => onSubmit()}>
						{t('button.add')}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
	return <>{formContent}</>;
}
